import React, { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { canUse, consume, getRemaining, isGuest } from "@/lib/guest";
import GuestBanner from "@/components/GuestBanner";
import BackButton from "@/components/BackButton";
import { useAuth } from "@/lib/auth";
import { Camera, Upload, Loader2, MapPin, Grape, Wine, BookOpen, ShoppingBag, Sparkles, ExternalLink, RotateCcw, Building2, NotebookPen } from "lucide-react";
import { toast } from "sonner";

const LINK_ICONS = {
  cepage: Grape,
  appellation: Wine,
  map: MapPin,
  poi: BookOpen,
  producer: Building2,
};

export default function Explorer() {
  const { lang } = useT();
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [remaining, setRemaining] = useState(getRemaining("explorer"));
  const [tastingBusy, setTastingBusy] = useState(false);

  const sendToTasting = async () => {
    if (!user) {
      navigate("/register?next=/explorer");
      toast.info(lang === "fr" ? "Créez un compte gratuit pour sauvegarder votre dégustation." : "Sign up to save your tasting.");
      return;
    }
    if (!result) return;
    setTastingBusy(true);
    try {
      // Draft a tasting pre-filled from the Vision result. The Carnet page
      // opens it in edit mode via the ?edit={id} query param so the user
      // can complete robe/nez/bouche/note before finalising.
      const detected = result.detected || {};
      const payload = {
        product_type: "Vin",
        name: (detected.appellations && detected.appellations[0]) ||
              result.scene_summary || "Dégustation Vision",
        photo_url: preview || "",
        raw_notes: [result.scene_summary, result.narration, result.tasting_hint]
                   .filter(Boolean).join("\n\n"),
        region: detected.region || null,
        appellation: (detected.appellations && detected.appellations[0]) || null,
        grapes: Array.isArray(detected.cepages) && detected.cepages.length ? detected.cepages : null,
      };
      const { data } = await api.post("/tastings", payload);
      toast.success(lang === "fr" ? "Ouverture du carnet…" : "Opening your notebook…");
      navigate(`/carnet?edit=${encodeURIComponent(data.id)}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || (lang === "fr" ? "Erreur d'enregistrement" : "Save failed"));
    } finally {
      setTastingBusy(false);
    }
  };

  const onPick = (file) => {
    if (!file) return;
    if (file.size > 12 * 1024 * 1024) {
      toast.error(lang === "fr" ? "Image trop lourde (max 12 Mo)" : "Image too large (max 12 MB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setPreview(reader.result);
      analyze(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const analyze = async (dataUrl) => {
    if (!canUse("explorer")) {
      setRemaining(0);
      toast.error(lang === "fr" ? "Quota gratuit atteint. Créez un compte." : "Free quota reached. Sign up.");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/explorer/analyze", { image_base64: dataUrl, lang });
      setResult(data);
      const c = consume("explorer");
      setRemaining(c.remaining === Infinity ? null : c.remaining);
    } catch (e) {
      const msg = e?.response?.data?.detail || (lang === "fr" ? "Impossible d'analyser cette photo." : "Could not analyse this photo.");
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setPreview(null);
    setResult(null);
  };

  return (
    <div className="max-w-4xl mx-auto px-5 md:px-10 py-10 md:py-14">
      <BackButton fallback="/discover" className="mb-4" />
      <div className="max-w-2xl">
        <span className="chip-pink">Explorer</span>
        <h1 className="font-display text-4xl md:text-5xl text-[#1F3A5F] mt-4 leading-[1.05]">
          {lang === "fr" ? "Raconte-moi cette photo" : "Tell me about this photo"}
        </h1>
        <p className="text-[#4A5568] mt-3">
          {lang === "fr"
            ? "Une vigne, une bouteille, un coteau, un verre — prenez une photo, geoVINUM AI vous la raconte comme un guide œnotouristique."
            : "A vine, a bottle, a hillside, a glass — snap a photo, geoVINUM AI narrates it like a wine-tourism guide."}
        </p>
      </div>

      {isGuest() && <div className="mt-6"><GuestBanner feature="explorer" remaining={remaining} /></div>}

      {!preview && !loading && (
        <div className="mt-8 rounded-3xl border-2 border-dashed border-[#1F3A5F]/20 bg-[#FBF7F3] p-8 md:p-12 text-center">
          <Camera className="w-10 h-10 text-[#E5276A] mx-auto" />
          <h2 className="font-display text-xl md:text-2xl text-[#1F3A5F] mt-4">
            {lang === "fr" ? "Choisissez une photo" : "Choose a photo"}
          </h2>
          <p className="text-sm text-[#4A5568] mt-1 max-w-sm mx-auto">
            {lang === "fr"
              ? "Plus la photo capte de contexte (paysage, étiquette, vigne), plus la narration est riche."
              : "The more context the photo captures, the richer the narration."}
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center items-center">
            <button
              data-testid="explorer-take-photo"
              onClick={() => cameraRef.current?.click()}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#E5276A] text-white text-sm font-bold hover:bg-[#C01661]"
            >
              <Camera className="w-4 h-4" /> {lang === "fr" ? "Prendre une photo" : "Take photo"}
            </button>
            <button
              data-testid="explorer-import-photo"
              onClick={() => fileRef.current?.click()}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white border border-[#1F3A5F]/15 text-[#1F3A5F] text-sm font-bold hover:border-[#E5276A]"
            >
              <Upload className="w-4 h-4" /> {lang === "fr" ? "Importer une photo" : "Upload photo"}
            </button>
          </div>
          <input
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            data-testid="explorer-camera-input"
            className="hidden"
            onChange={(e) => onPick(e.target.files?.[0])}
          />
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            data-testid="explorer-file-input"
            className="hidden"
            onChange={(e) => onPick(e.target.files?.[0])}
          />
        </div>
      )}

      {preview && (
        <div className="mt-8 grid lg:grid-cols-[1fr_2fr] gap-8 items-start">
          <div className="sticky top-6 self-start">
            <img
              src={preview}
              alt="Selected"
              data-testid="explorer-preview"
              className="w-full rounded-2xl object-cover border border-[#1F3A5F]/12 shadow-sm"
            />
            <button
              onClick={reset}
              data-testid="explorer-reset"
              className="mt-3 w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-white border border-[#1F3A5F]/15 text-[#1F3A5F] text-xs font-bold hover:border-[#E5276A]"
            >
              <RotateCcw className="w-3.5 h-3.5" /> {lang === "fr" ? "Nouvelle photo" : "New photo"}
            </button>
          </div>

          <div className="min-w-0">
            {loading && (
              <div className="rounded-2xl border border-[#1F3A5F]/12 bg-white p-8 text-center" data-testid="explorer-loading">
                <Loader2 className="w-7 h-7 text-[#E5276A] mx-auto animate-spin" />
                <p className="mt-3 font-display text-lg text-[#1F3A5F]">
                  {lang === "fr" ? "geoVINUM AI observe la scène…" : "geoVINUM AI is observing the scene…"}
                </p>
                <p className="text-xs text-[#8B95A1] mt-1">{lang === "fr" ? "Cela peut prendre 10-20 secondes" : "This may take 10-20 seconds"}</p>
              </div>
            )}

            {result && (
              <div className="space-y-6" data-testid="explorer-result">
                {result.scene_summary && (
                  <div className="rounded-2xl bg-[#FBF7F3] border-l-[3px] border-[#E5276A] p-4">
                    <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#E5276A]">
                      {lang === "fr" ? "Ce que geoVINUM AI voit" : "What geoVINUM AI sees"}
                    </p>
                    <p className="mt-1 font-display text-lg text-[#1F3A5F]">{result.scene_summary}</p>
                  </div>
                )}

                {/* Narration */}
                <article className="prose-narration" data-testid="explorer-narration">
                  {(result.narration || "").split(/\n\n+/).map((para, i) => (
                    <p key={i} className="text-[#1F3A5F] text-[16px] leading-[1.75] mb-4 font-[Lato]">{para}</p>
                  ))}
                </article>

                {/* Save to tasting notebook */}
                <div className="rounded-2xl border border-[#1F3A5F]/15 bg-white p-4 flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <p className="font-bold text-[#1F3A5F] inline-flex items-center gap-1.5">
                      <NotebookPen className="w-4 h-4 text-[#E5276A]" />
                      {lang === "fr" ? "Déguster cette bouteille ?" : "Taste this bottle?"}
                    </p>
                    <p className="text-xs text-[#4A5568] mt-0.5">
                      {lang === "fr"
                        ? "Ajoute une fiche à ton carnet, pré-remplie avec la photo et l'analyse IA."
                        : "Add a card to your notebook, pre-filled with the photo and the AI analysis."}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={sendToTasting}
                    disabled={tastingBusy}
                    data-testid="explorer-taste-btn"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#E5276A] text-white text-sm font-bold hover:bg-[#C01661] disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
                  >
                    {tastingBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <NotebookPen className="w-4 h-4" />}
                    {lang === "fr" ? "Déguster" : "Taste"}
                  </button>
                </div>

                {result.fun_fact && (
                  <div className="rounded-2xl bg-[#1F3A5F] text-white p-5">
                    <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#FFD8E5]">
                      {lang === "fr" ? "Le saviez-vous ?" : "Did you know?"}
                    </div>
                    <p className="mt-1 text-sm md:text-base leading-relaxed">{result.fun_fact}</p>
                  </div>
                )}

                {result.tasting_hint && (
                  <div className="rounded-2xl border border-[#1F3A5F]/12 bg-white p-5 flex items-start gap-3">
                    <Wine className="w-5 h-5 text-[#E5276A] mt-0.5 shrink-0" />
                    <div>
                      <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#E5276A]">
                        {lang === "fr" ? "Pour la dégustation" : "Tasting tip"}
                      </div>
                      <p className="text-sm text-[#1F3A5F] mt-1">{result.tasting_hint}</p>
                    </div>
                  </div>
                )}

                {/* Pour aller plus loin */}
                {(result.deeper_links?.length > 0) && (
                  <section data-testid="explorer-deeper-links">
                    <h3 className="font-display text-xl text-[#1F3A5F] inline-flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-[#E5276A]" />
                      {lang === "fr" ? "Pour aller plus loin" : "Go deeper"}
                    </h3>
                    <div className="mt-3 grid sm:grid-cols-2 gap-3">
                      {result.deeper_links.map((l) => {
                        const Icon = LINK_ICONS[l.type] || BookOpen;
                        const href =
                          l.type === "cepage" ? `/cepages/${l.slug}` :
                          l.type === "appellation" ? `/appellations/${l.slug}` :
                          l.type === "producer" ? `/producers/${l.slug}` :
                          l.type === "map" ? `/pro?q=${encodeURIComponent(l.label)}` :
                          l.type === "poi" ? `/nearby?q=${encodeURIComponent(l.label)}` :
                          "#";
                        return (
                          <Link
                            key={`${l.type}-${l.slug}`}
                            to={href}
                            data-testid={`explorer-link-${l.type}-${l.slug}`}
                            className="group flex items-start gap-3 rounded-xl border border-[#1F3A5F]/12 bg-white p-4 hover:border-[#E5276A] transition-colors"
                          >
                            <div className="w-9 h-9 rounded-lg bg-[#FCE4ED] flex items-center justify-center shrink-0">
                              <Icon className="w-4 h-4 text-[#E5276A]" />
                            </div>
                            <div className="min-w-0">
                              <div className="text-[10px] uppercase tracking-wider font-bold text-[#8B95A1]">{l.type}</div>
                              <div className="font-bold text-[#1F3A5F] group-hover:text-[#E5276A] transition-colors">{l.label}</div>
                              {l.description && <p className="text-xs text-[#4A5568] mt-0.5 line-clamp-2">{l.description}</p>}
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  </section>
                )}

                {/* Shop suggestions */}
                {(result.shop_suggestions?.length > 0) && (
                  <section data-testid="explorer-shop">
                    <h3 className="font-display text-xl text-[#1F3A5F] inline-flex items-center gap-2">
                      <ShoppingBag className="w-4 h-4 text-[#E5276A]" />
                      {lang === "fr" ? "Au shop geoVINUM" : "From the geoVINUM shop"}
                    </h3>
                    <div className="mt-3 grid sm:grid-cols-3 gap-3">
                      {result.shop_suggestions.map((p) => (
                        <a
                          key={p.id}
                          href={p.url}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={`explorer-shop-${p.id}`}
                          className="group rounded-xl border border-[#1F3A5F]/12 bg-white overflow-hidden hover:border-[#E5276A]"
                        >
                          {p.image && (
                            <div className="aspect-[4/3] overflow-hidden bg-[#FBF7F3]">
                              <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                            </div>
                          )}
                          <div className="p-3">
                            <div className="font-bold text-sm text-[#1F3A5F] group-hover:text-[#E5276A] line-clamp-2">{p.name}</div>
                            <div className="text-xs text-[#8B95A1] mt-1 inline-flex items-center gap-1">
                              {p.price && <span className="text-[#E5276A] font-bold">{p.price}</span>}
                              <ExternalLink className="w-3 h-3 ml-auto" />
                            </div>
                          </div>
                        </a>
                      ))}
                    </div>
                  </section>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
